import type { components } from "@oathera/contract/api";
import type { Measure, StatusLevel } from "../../components/index.ts";

/**
 * Presentation vocabulary for the Service page (task 11.5; design 12.2 Service row, 12.3 principles).
 *
 * It keeps the one-way maps from contract enums/shapes onto the screen's presentational pieces in a
 * single, testable place: how a `Quantity`'s exactness becomes an exact/estimate `Measure`, how a
 * `CoreState` and an instance's `state`/`stale` become a `StatusBadge` level (unverifiable is never
 * green, design 12.3), the golden-signal descriptors, and — most importantly — how the contract's
 * `AdminLink` tri-state becomes the three distinct UI states the admin deep-link must render.
 */

type Quantity = components["schemas"]["Quantity"];
type AdminLink = components["schemas"]["AdminLink"];
type Instance = components["schemas"]["Instance"];
type CoreState = NonNullable<Instance["core_state"]>;

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // No data cannot be verified: neither an exact counter nor a sampled estimate, so never green.
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/* ---- Golden signals ------------------------------------------------------------------------- */

/** A golden signal rendered on the page: its contract key, label, unit and value formatter. */
export interface GoldenSignalSpec {
  readonly key: keyof components["schemas"]["GoldenSignals"];
  readonly title: string;
  readonly unit: string;
  readonly format: (value: number) => string;
}

const RATE = (v: number): string => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
const RATIO = (v: number): string => `${(v * 100).toFixed(2)}%`;
const MS = (v: number): string => v.toLocaleString(undefined, { maximumFractionDigits: 1 });

/**
 * The four golden signals (design 12.2 "golden signals"), expressed from the exact fields the
 * contract's `GoldenSignals` carries: traffic (`rate_per_s`), errors (`error_ratio`), latency
 * (p50/p95/p99) and saturation. Latency is shown as three percentile rows under one heading rather
 * than inventing a single "latency" number the contract does not define.
 */
export const GOLDEN_SIGNALS: readonly GoldenSignalSpec[] = [
  { key: "rate_per_s", title: "Traffic", unit: "/s", format: RATE },
  { key: "error_ratio", title: "Errors", unit: "", format: RATIO },
  { key: "latency_p50_ms", title: "Latency p50", unit: " ms", format: MS },
  { key: "latency_p95_ms", title: "Latency p95", unit: " ms", format: MS },
  { key: "latency_p99_ms", title: "Latency p99", unit: " ms", format: MS },
  { key: "saturation", title: "Saturation", unit: "", format: RATE },
];

/** The four route-table signal columns (a compact subset of the golden signals, design 12.2). */
export const ROUTE_SIGNALS: readonly GoldenSignalSpec[] = [
  { key: "rate_per_s", title: "Traffic", unit: "/s", format: RATE },
  { key: "error_ratio", title: "Errors", unit: "", format: RATIO },
  { key: "latency_p95_ms", title: "p95", unit: " ms", format: MS },
  { key: "saturation", title: "Saturation", unit: "", format: RATE },
];

/* ---- Instances and Protected_Core state ----------------------------------------------------- */

/**
 * Map an instance's liveness onto a status level. A `silent` or `stale` instance is grey `unknown`,
 * never the healthy green (design 12.3): we have not heard from it recently, so "all good" would be
 * a lie. An `active`, non-stale instance is healthy.
 */
export function instanceLevel(instance: Instance): StatusLevel {
  if (instance.state === "silent" || instance.stale) return "unknown";
  return "healthy";
}

/** The visible liveness label for an instance (icon + text; the badge supplies the icon). */
export function instanceStateText(instance: Instance): string {
  if (instance.state === "silent") return "Silent";
  if (instance.stale) return "Stale";
  return "Active";
}

/**
 * Map a Protected_Core state onto a status level. `verified` is healthy; `degraded` warns; `failed`
 * and `absent` are errors; the catch-all `other` and a missing state are grey `unknown` (never
 * green). A `null` core state (none reported) is handled by the caller as "no data".
 */
export function coreStateLevel(state: CoreState): StatusLevel {
  switch (state) {
    case "verified":
      return "healthy";
    case "degraded":
      return "warn";
    case "failed":
    case "absent":
      return "error";
    case "other":
      return "unknown";
  }
}

/** The visible label for a Protected_Core state. */
export function coreStateText(state: CoreState | null): string {
  if (state === null) return "No data";
  switch (state) {
    case "verified":
      return "Verified";
    case "degraded":
      return "Degraded";
    case "failed":
      return "Failed";
    case "absent":
      return "Absent";
    case "other":
      return "Other";
  }
}

/* ---- Admin deep-link tri-state (THE named behaviour; FR-1.5) --------------------------------- */

/**
 * The three distinct states the admin deep-link element must render, derived from the contract's
 * `AdminLink` discriminated union (`registration` is `unknown` | `registered` | `none`):
 *
 * - `unknown`      — registration status is not yet known (before the data loads, or the response
 *                    carries `registration: "unknown"`). The element is a DISABLED PLACEHOLDER. This
 *                    is the DEFAULT: anything we cannot positively classify as the other two states
 *                    stays here, so the element never pretends to link somewhere it does not.
 * - `registered`  — a registered `admin_url` is known. The element is ENABLED and links to `url`.
 * - `none`         — registration is known to have NO url. The element is a DISABLED PLACEHOLDER
 *                    that says "none configured".
 *
 * The two disabled states (`unknown` vs `none`) look alike but MEAN different things; the component
 * keeps them distinct in the DOM and accessible name. If a future contract shape cannot be
 * classified, we fall through to `unknown` (least-committal, never a dead/enabled link).
 */
export type AdminLinkView =
  | { readonly kind: "unknown" }
  | { readonly kind: "registered"; readonly url: string }
  | { readonly kind: "none" };

/**
 * Derive the {@link AdminLinkView} from an `AdminLink`, or from `undefined` when the response has
 * not been read yet — both map to `unknown` so the element defaults to the disabled placeholder.
 *
 * A `registered` link is only treated as enabled when its `url` is a non-empty string WITH a safe
 * web scheme (`http`/`https`): a registered entry whose url is empty, unparseable, or carries a
 * non-http(s) scheme (e.g. `javascript:`, `file:`, `data:`) is downgraded to `unknown` (we were told
 * a url exists but got nothing we can render as a safe, live link, so we must not render an enabled,
 * dead or unsafe link). The url is otherwise treated as untrusted external data by the component when
 * it renders the anchor; this guard is a defence-in-depth scheme check, not a full URL blessing.
 */
export function adminLinkView(link: AdminLink | undefined): AdminLinkView {
  if (link === undefined) return { kind: "unknown" };
  switch (link.registration) {
    case "registered": {
      const url = typeof link.url === "string" ? link.url.trim() : "";
      return url.length > 0 && isHttpUrl(url) ? { kind: "registered", url } : { kind: "unknown" };
    }
    case "none":
      return { kind: "none" };
    case "unknown":
      return { kind: "unknown" };
    default:
      // A shape we do not recognise is never enabled; default to the disabled placeholder.
      return { kind: "unknown" };
  }
}

/**
 * True when `url` parses as an absolute `http:`/`https:` URL. A relative, unparseable or non-web
 * scheme (`javascript:`, `data:`, `file:`, `mailto:`, …) is rejected so the admin deep-link is never
 * rendered as an enabled anchor pointing at something that is not a safe navigation target.
 */
function isHttpUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}
